import { lazy, Suspense } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Button, Card, Group, SimpleGrid, Stack, Table, Text } from '@mantine/core';
import {
  useDashboardControllerSummary,
  useHealthControllerCheck,
  useActivityControllerList,
  useAttendanceControllerNow,
} from '@werkbank/shared';
import { DOC_TYPE_LABELS, formatCents } from '../lib/format';
import { useHasFlag } from '../lib/permissions';
import {
  ACTIVITY_CATEGORY_LABELS,
  ACTIVITY_KIND_LABELS,
  activityCategoryColor,
  formatTimeDE,
} from '../lib/activity';
import { StatusBadge } from '../components/StatusBadge';
import { AvatarChip } from '../components/AvatarChip';
import { PageSkeleton } from '../components/PageSkeleton';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { HausmeisterWartelisteCard } from '../components/HausmeisterWartelisteCard';

// Vollanalyse M18: das Umsatz-Chart (recharts) lädt lazy nach — recharts fällt
// damit aus dem Initial-Bundle (das schon auf der LoginPage geladen wird).
const RevenueAreaChart = lazy(() =>
  import('../components/RevenueAreaChart').then((m) => ({ default: m.RevenueAreaChart })),
);

/** KPI-Karte (Brief §7): Label 14 sekundär, Wert 30 bold tnum. */
function KpiCard({
  label,
  value,
  sub,
  subColor,
  testId,
}: {
  label: string;
  value: string;
  sub?: string;
  subColor?: string;
  testId?: string;
}) {
  return (
    <Card data-testid={testId}>
      <Text size="sm" c="dimmed">
        {label}
      </Text>
      <Text
        className="wb-tnum"
        fw={700}
        style={{ fontSize: 'var(--wb-font-size-kpi)', lineHeight: 1.2 }}
      >
        {value}
      </Text>
      {sub && (
        <Text
          size="sm"
          style={subColor ? { color: subColor } : undefined}
          c={subColor ? undefined : 'dimmed'}
        >
          {sub}
        </Text>
      )}
    </Card>
  );
}

/** Kategorie-Punkt vor einer Aktivitäts-Zeile. */
function ActivityDot({ category }: { category: string }) {
  return (
    <span
      style={{
        display: 'inline-block',
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: activityCategoryColor(category),
        flex: 'none',
      }}
    />
  );
}

/**
 * Widget „Letzte Aktivitäten" (P2-C inc.6): die neuesten Betriebs-Ereignisse aus
 * der Activity-Query (nicht der Summary-Query), alle 30 s aktualisiert. Klick auf
 * einen Auftragseintrag öffnet den Auftrag.
 */
function RecentActivityWidget() {
  const navigate = useNavigate();
  const activity = useActivityControllerList({ limit: 8 }, { query: { refetchInterval: 30_000 } });
  const entries = activity.data?.entries ?? [];
  return (
    <Card padding={24} data-testid="dash-activity">
      <Group justify="space-between" mb="sm">
        <Text fw={600} size="lg">
          Letzte Aktivitäten
        </Text>
        <Text component={Link} to="/activity" size="sm" c="var(--wb-primary-600)">
          Alle ansehen
        </Text>
      </Group>
      {activity.isLoading ? (
        <Text size="sm" c="dimmed">
          lädt…
        </Text>
      ) : entries.length === 0 ? (
        <EmptyState text="Noch keine Aktivitäten." />
      ) : (
        <Stack gap={8}>
          {entries.map((e) => (
            <Group
              key={e.id}
              gap="xs"
              wrap="nowrap"
              align="flex-start"
              style={e.orderId ? { cursor: 'pointer' } : undefined}
              onClick={
                e.orderId
                  ? () => void navigate({ to: '/orders/$orderId', params: { orderId: e.orderId! } })
                  : undefined
              }
            >
              <span style={{ marginTop: 6 }}>
                <ActivityDot category={e.category} />
              </span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <Text size="sm" fw={500} lineClamp={1}>
                  {ACTIVITY_KIND_LABELS[e.kind] ?? e.kind}
                  {e.orderNumber ? ` · ${e.orderNumber}` : ''}
                </Text>
                <Text size="xs" c="dimmed" lineClamp={1}>
                  {[e.actorName, ACTIVITY_CATEGORY_LABELS[e.category], e.detail]
                    .filter(Boolean)
                    .join(' · ')}
                </Text>
              </div>
              <Text size="xs" c="dimmed" className="wb-tnum" style={{ whiteSpace: 'nowrap' }}>
                {formatTimeDE(e.at)}
              </Text>
            </Group>
          ))}
        </Stack>
      )}
    </Card>
  );
}

/**
 * Widget „Wer arbeitet gerade" (P2-C inc.7, ortungsfrei): anwesende Mitarbeiter
 * mit Zustand, „seit HH:MM", laufendem Auftrag + Ort (Text). Darunter ein
 * Konflikt-Hinweis mit Link zu den Büro-Korrekturen. Alle 30 s aktualisiert.
 */
function WhoWorksNowWidget() {
  const board = useAttendanceControllerNow({ query: { refetchInterval: 30_000 } });
  const now = board.data?.now ?? [];
  const conflicts = board.data?.conflicts ?? [];
  // P2-M9 §4: Bereitschafts-Marker von der Plantafel — reine Sichtbarkeit.
  const standbyToday = board.data?.standbyToday ?? [];
  return (
    <Card padding={24} data-testid="dash-now">
      <Text fw={600} mb="sm">
        Wer arbeitet gerade
      </Text>
      {board.isLoading ? (
        <Text size="sm" c="dimmed">
          lädt…
        </Text>
      ) : now.length === 0 ? (
        <EmptyState text="Zurzeit ist niemand eingestempelt." />
      ) : (
        <Stack gap={10}>
          {now.map((r) => (
            <Group key={r.userId} gap="xs" wrap="nowrap" align="flex-start">
              <AvatarChip name={r.displayName} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <Group gap="xs" wrap="nowrap">
                  <Text size="sm" fw={500} truncate>
                    {r.displayName}
                  </Text>
                  {/* D-W1 QS-5: Tint-Pill statt roher Mantine-Badge. */}
                  {r.state === 'PRESENT' ? (
                    <StatusBadge status="PAID" label="Anwesend" />
                  ) : (
                    <StatusBadge status="REVIEW" label="Pause" />
                  )}
                  <Text size="xs" c="dimmed">
                    seit {formatTimeDE(r.sinceIso)}
                    {/* Faruq 2026-08-26: Tages-Kommen zusätzlich — „seit" ist
                        nur der Beginn des aktuellen Zustands (z. B. nach der
                        Pause); nicht doppeln, wenn beide gleich sind. */}
                    {r.kommenIso && formatTimeDE(r.kommenIso) !== formatTimeDE(r.sinceIso)
                      ? ` · gekommen ${formatTimeDE(r.kommenIso)}`
                      : ''}
                  </Text>
                </Group>
                {r.currentOrder && (
                  <Text size="xs" c="dimmed" lineClamp={1}>
                    {r.currentOrder.number} · {r.currentOrder.title}
                    {r.currentOrder.siteText ? ` · ${r.currentOrder.siteText}` : ''}
                  </Text>
                )}
              </div>
            </Group>
          ))}
        </Stack>
      )}
      {/* P2-M9 §4: „Bereitschaft heute: {Namen}" — nur wenn nicht leer. */}
      {standbyToday.length > 0 && (
        <Group gap={6} mt="md" data-testid="dash-standby-today">
          <Text size="sm" c="dimmed">
            Bereitschaft heute:
          </Text>
          {standbyToday.map((name) => (
            <span key={name} className="wb-standby-pill" style={{ cursor: 'default' }}>
              {name}
            </span>
          ))}
        </Group>
      )}
      {conflicts.length > 0 && (
        <Text
          component={Link}
          to="/attendance"
          size="sm"
          c="var(--wb-status-overdue)"
          mt="md"
          display="block"
          data-testid="dash-now-conflicts"
        >
          {conflicts.length} offene {conflicts.length === 1 ? 'Stempel' : 'Stempel'} prüfen
        </Text>
      )}
    </Card>
  );
}

/** Dashboard v1 (B2-0, D-M4 vorgezogen): KPIs, Umsatz-Flächenchart, Listen. */
export function DashboardPage() {
  const navigate = useNavigate();
  const summary = useDashboardControllerSummary();
  // „Aktivitäten" und „Wer arbeitet gerade" stehen unter viewAnalytics — nur mit
  // Flag rendern (und damit abfragen), sonst gäbe es 403 auf den Widgets.
  const canViewActivity = useHasFlag('viewAnalytics');
  useHealthControllerCheck({ query: { retry: false, refetchInterval: 60_000 } });

  if (summary.isLoading) return <PageSkeleton />;
  if (summary.isError || !summary.data) return <Text c="red">Dashboard nicht verfügbar.</Text>;
  const d = summary.data;

  const chartData = d.revenueByMonth.map((m) => ({ ...m, euro: m.netCents / 100 }));

  return (
    <Stack gap="lg">
      <PageHeader
        title="Übersicht"
        meta={`${d.revenueMonth.monthLabel} · ${d.openItems.count} offene Posten`}
        action={
          /* Schnellknoepfe (Faruq 2026-08-24): die Uebersicht ist die
             Landeseite am Morgen — von hier aus faengt Arbeit an. Bewusst nur
             die drei ANLAGE-Wege; alles andere ist einen Klick in der
             Seitenleiste entfernt und braucht hier keine zweite Tuer. Der
             Auftrag steht vorn, weil er der haeufigste Anfang ist. */
          <Group gap="xs">
            <Button
              onClick={() => void navigate({ to: '/orders', search: { neu: '1' } })}
              data-testid="dash-new-order"
            >
              Neuer Auftrag
            </Button>
            <Button
              variant="default"
              onClick={() => void navigate({ to: '/quotes', search: { neu: '1' } })}
              data-testid="dash-new-quote"
            >
              Neues Angebot
            </Button>
            <Button
              variant="default"
              onClick={() => void navigate({ to: '/customers', search: { neu: '1' } })}
              data-testid="dash-new-customer"
            >
              Neuer Kunde
            </Button>
          </Group>
        }
      />

      <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="xl">
        <KpiCard
          label="Offene Posten"
          value={`${formatCents(d.openItems.sumCents)} €`}
          sub={
            d.openItems.overdueCount > 0
              ? `${d.openItems.overdueCount} überfällig (${formatCents(d.openItems.overdueSumCents)} €)`
              : `${d.openItems.count} Rechnungen, keine überfällig`
          }
          subColor={d.openItems.overdueCount > 0 ? 'var(--wb-status-overdue)' : undefined}
          testId="kpi-open"
        />
        <KpiCard
          label={`Umsatz ${d.revenueMonth.monthLabel} (netto)`}
          value={`${formatCents(d.revenueMonth.netCents)} €`}
          sub="festgeschriebene Rechnungen abzüglich Gutschriften"
          testId="kpi-revenue"
        />
        <KpiCard
          label="Fertig, nicht abgerechnet"
          value={String(d.doneUnbilled.count)}
          sub="Abgeschlossene Aufträge ohne Schlussrechnung"
          testId="kpi-unbilled"
        />
        {/* D-W1 DA-2: neutraler Beschreibungstext bleibt neutral — Signalfarbe
            nur dort, wo das Wort das Signal trägt („überfällig" oben). */}
        <KpiCard
          label="Fällige Wiedervorlagen"
          value={String(d.dueFollowUps.count)}
          sub="offene Angebote zur Nachfassung"
          testId="kpi-followups"
        />
      </SimpleGrid>

      {/* Tobias 2026-10-04: worauf beim Hausmeister noch gewartet wird. */}
      <HausmeisterWartelisteCard />

      <Card padding={24} data-testid="revenue-chart">
        <Text fw={600} mb="sm">
          Umsatz (netto), letzte 12 Monate
        </Text>
        {/* Vollanalyse M18: fixe Höhe bleibt am Wrapper — während des
            Nachladens steht hier eine leere Box gleicher Höhe (kein Springen). */}
        <div style={{ width: '100%', height: 260 }}>
          <Suspense fallback={null}>
            <RevenueAreaChart data={chartData} />
          </Suspense>
        </div>
      </Card>

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
        <Card padding={24} data-testid="overdue-card">
          <Text fw={600} mb="sm">
            Überfällige offene Posten
          </Text>
          {d.overdueList.length === 0 ? (
            <EmptyState text="Nichts überfällig. Weiter so." />
          ) : (
            <Table className="wb-table-dense">
              <Table.Tbody>
                {d.overdueList.map((o) => (
                  <Table.Tr
                    key={o.documentId}
                    style={{ cursor: 'pointer' }}
                    onClick={() =>
                      void navigate({
                        to: '/documents/$documentId',
                        params: { documentId: o.documentId },
                      })
                    }
                  >
                    <Table.Td className="wb-tnum">{o.number}</Table.Td>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        <AvatarChip name={o.customerName} />
                        {o.customerName}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <StatusBadge status="OVERDUE" label={`${o.daysOverdue} Tage`} />
                    </Table.Td>
                    <Table.Td className="wb-tnum" style={{ textAlign: 'right' }}>
                      {formatCents(o.openCents)} €
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Card>

        <Card padding={24} data-testid="recent-card">
          <Text fw={600} mb="sm">
            Zuletzt festgeschrieben
          </Text>
          {d.recentFinalized.length === 0 ? (
            <EmptyState text="Noch keine festgeschriebenen Belege." />
          ) : (
            <Table className="wb-table-dense">
              <Table.Tbody>
                {d.recentFinalized.map((r) => (
                  <Table.Tr
                    key={r.documentId}
                    style={{ cursor: 'pointer' }}
                    onClick={() =>
                      void navigate({
                        to: '/documents/$documentId',
                        params: { documentId: r.documentId },
                      })
                    }
                  >
                    <Table.Td>
                      <Text size="sm">{DOC_TYPE_LABELS[r.type]}</Text>
                    </Table.Td>
                    <Table.Td className="wb-tnum">{r.number}</Table.Td>
                    <Table.Td>
                      <StatusBadge status={r.status} />
                    </Table.Td>
                    <Table.Td className="wb-tnum" style={{ textAlign: 'right' }}>
                      {formatCents(r.grossTotalCents)} €
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Card>
      </SimpleGrid>

      {canViewActivity && (
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
          <WhoWorksNowWidget />
          <RecentActivityWidget />
        </SimpleGrid>
      )}
    </Stack>
  );
}
